import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Plus, Trash2, Save } from "lucide-react";

const DAYS = [
  { key: "0", label: "Monday" }, { key: "1", label: "Tuesday" },
  { key: "2", label: "Wednesday" }, { key: "3", label: "Thursday" },
  { key: "4", label: "Friday" }, { key: "5", label: "Saturday" },
  { key: "6", label: "Sunday" },
];
const empty = { "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] };

export default function WeeklyAvailability() {
  const { currentOrgId } = useAuth();
  const [rules, setRules] = useState(empty);
  const [timezone, setTimezone] = useState("UTC");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (currentOrgId) load(); }, [currentOrgId]);

  const load = async () => {
    try {
      const r = await apiFetch(`/scheduling/weekly-availability?organization_id=${currentOrgId}`);
      setRules(r.weekly_rules || empty);
      setTimezone(r.timezone || "UTC");
    } catch (e) { toast.error(e.message); }
  };

  const toggle = (day) => {
    setRules((r) => ({ ...r, [day]: r[day]?.length ? [] : [{ start: "09:00", end: "17:00" }] }));
  };
  const addRange = (day) => setRules((r) => ({ ...r, [day]: [...(r[day] || []), { start: "09:00", end: "17:00" }] }));
  const updateRange = (day, idx, field, value) =>
    setRules((r) => ({ ...r, [day]: r[day].map((rg, i) => i === idx ? { ...rg, [field]: value } : rg) }));
  const removeRange = (day, idx) => setRules((r) => ({ ...r, [day]: r[day].filter((_, i) => i !== idx) }));
  const copyTo = (fromDay, toDay) => setRules((r) => ({ ...r, [toDay]: JSON.parse(JSON.stringify(r[fromDay] || [])) }));
  const applyWeekdays = (fromDay) => {
    const src = JSON.parse(JSON.stringify(rules[fromDay] || []));
    setRules((r) => ({ ...r, "0": src, "1": src, "2": src, "3": src, "4": src }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/scheduling/weekly-availability", {
        method: "PUT",
        body: JSON.stringify({ organization_id: currentOrgId, weekly_rules: rules, timezone }),
      });
      toast.success("Availability saved");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const tzList = ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
                  "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore",
                  "Australia/Sydney"];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-5 md:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h2 className="font-display font-semibold text-lg tracking-tight">Set your weekly hours</h2>
            <p className="text-xs text-[#57534E]">Your recurring availability. Applied every week until you change it.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#57534E]">Timezone</label>
            <select data-testid="weekly-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-[#E8E2D9] text-sm bg-white">
              {tzList.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
            <Button data-testid="weekly-save" onClick={save} disabled={saving}
              className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white">
              <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {DAYS.map((d) => {
            const on = (rules[d.key] || []).length > 0;
            return (
              <div key={d.key} className="border border-[#E8E2D9] rounded-2xl p-3 md:p-4"
                   data-testid={`weekly-day-${d.key}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-[130px]">
                    <Switch checked={on} onCheckedChange={() => toggle(d.key)}
                            data-testid={`weekly-toggle-${d.key}`} />
                    <span className={`text-sm font-medium ${on ? "text-[#1C1917]" : "text-[#A8A29E]"}`}>{d.label}</span>
                  </div>
                  <div className="flex-1 min-w-[280px] space-y-2">
                    {!on ? (
                      <div className="text-xs text-[#A8A29E]">Unavailable</div>
                    ) : (
                      <>
                        {(rules[d.key] || []).map((rg, idx) => (
                          <div key={idx} className="flex items-center gap-2 flex-wrap">
                            <Input type="time" value={rg.start}
                                   onChange={(e) => updateRange(d.key, idx, "start", e.target.value)}
                                   className="w-28" data-testid={`weekly-start-${d.key}-${idx}`} />
                            <span className="text-xs text-[#A8A29E]">to</span>
                            <Input type="time" value={rg.end}
                                   onChange={(e) => updateRange(d.key, idx, "end", e.target.value)}
                                   className="w-28" data-testid={`weekly-end-${d.key}-${idx}`} />
                            <button onClick={() => removeRange(d.key, idx)}
                                    data-testid={`weekly-remove-${d.key}-${idx}`}
                                    className="p-1.5 rounded-lg hover:bg-[#F5F5F4] text-[#B02222]">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-2 pt-1 flex-wrap">
                          <button onClick={() => addRange(d.key)} data-testid={`weekly-addrange-${d.key}`}
                                  className="text-xs px-2 py-1 rounded-lg border border-[#E8E2D9] hover:border-[#E8712C] flex items-center gap-1">
                            <Plus size={12} /> Add range
                          </button>
                          <select onChange={(e) => { if (e.target.value) { copyTo(d.key, e.target.value); e.target.value = ""; } }}
                                  data-testid={`weekly-copy-${d.key}`}
                                  className="text-xs px-2 py-1 rounded-lg border border-[#E8E2D9] bg-white">
                            <option value="">Copy to…</option>
                            {DAYS.filter((x) => x.key !== d.key).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                          </select>
                          <button onClick={() => applyWeekdays(d.key)} data-testid={`weekly-apply-weekdays-${d.key}`}
                                  className="text-xs px-2 py-1 rounded-lg border border-[#E8E2D9] hover:border-[#E8712C] flex items-center gap-1">
                            <Copy size={12} /> Apply to weekdays
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
